import React from 'react';
import { Shield, Brain, Code, Globe, Cpu, Lock, Smartphone, Cloud, Database, Radio, Leaf, Users } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useLanguage } from '../context/LanguageContext';

const PurposePage = () => {
  const { t } = useLanguage();

  const techAreas = [
    { icon: Shield, titleTr: 'Siber Güvenlik', titleEn: 'Cybersecurity', color: '#4DFF88' },
    { icon: Brain, titleTr: 'Yapay Zekâ ve Veri Bilimi', titleEn: 'AI & Data Science', color: '#4DD6FF' },
    { icon: Code, titleTr: 'Prompt Mühendisliği ve Generatif AI', titleEn: 'Prompt Engineering & Generative AI', color: '#FF4DA6' },
    { icon: Database, titleTr: 'Büyük Dil Modelleri (LLM)', titleEn: 'Large Language Models (LLM)', color: '#4DFF88' },
    { icon: Cpu, titleTr: 'Otomasyon ve MLOps', titleEn: 'Automation & MLOps', color: '#4DD6FF' },
    { icon: Lock, titleTr: 'Malware Analizi ve Reverse Engineering', titleEn: 'Malware Analysis & Reverse Engineering', color: '#FF4DA6' },
    { icon: Shield, titleTr: 'Antivirüs ve Endpoint Security', titleEn: 'Antivirus & Endpoint Security', color: '#4DFF88' },
    { icon: Code, titleTr: 'Yazılım Geliştirme ve Güvenli Kodlama', titleEn: 'Software Development & Secure Coding', color: '#4DD6FF' },
    { icon: Cloud, titleTr: 'Web ve Bulut Teknolojileri', titleEn: 'Web & Cloud Technologies', color: '#FF4DA6' },
    { icon: Database, titleTr: 'Blockchain ve Kriptoloji', titleEn: 'Blockchain & Cryptology', color: '#4DFF88' },
    { icon: Smartphone, titleTr: 'Mobil Uygulama ve Oyun Geliştirme', titleEn: 'Mobile & Game Development', color: '#4DD6FF' },
    { icon: Globe, titleTr: 'Sanal ve Artırılmış Gerçeklik', titleEn: 'VR & AR', color: '#FF4DA6' },
    { icon: Radio, titleTr: 'Kablolu ve Kablosuz İletişim', titleEn: 'Wired & Wireless Communication', color: '#4DFF88' },
    { icon: Radio, titleTr: 'Uydu ve Telekomünikasyon', titleEn: 'Satellite & Telecommunications', color: '#4DD6FF' },
    { icon: Users, titleTr: 'Dijital Okuryazarlık', titleEn: 'Digital Literacy', color: '#FF4DA6' },
  ];

  const socialKeys = ['environment', 'intercultural', 'youth'];

  return (
    <div className="min-h-screen bg-[#060b1a]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#060b1a] via-[#0a1628] to-[#0f1f33]" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#4DD6FF]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-[#FF4DA6]/10 rounded-full blur-[120px]" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full border border-[#4DD6FF]/30 bg-[#4DD6FF]/5 text-[#4DD6FF] text-sm font-medium mb-6">
              {t('purpose.badge')}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
              {t('purpose.title')}
            </h1>
          </div>
        </div>
      </section>

      {/* Main Purpose */}
      <section className="relative py-16 bg-[#060b1a]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 rounded-2xl border border-white/10 bg-gradient-to-br from-[#4DFF88]/5 to-[#4DD6FF]/5 backdrop-blur-sm mb-16">
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('purpose.intro1')}
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('purpose.intro2')}
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              {t('purpose.intro3')}
            </p>
          </div>

          {/* Technology Areas */}
          <div className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-8 text-center">
              {t('purpose.techAreasTitle')}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {techAreas.map((area, index) => (
                <div
                  key={index}
                  className="group p-4 rounded-xl border border-white/10 bg-white/5 hover:border-white/20 transition-all duration-300 text-center"
                >
                  <div 
                    className="w-10 h-10 rounded-lg mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${area.color}15` }}
                  >
                    <area.icon className="w-5 h-5" style={{ color: area.color }} />
                  </div>
                  <h3 className="text-sm font-medium text-white">
                    {t('header.home') === 'Home' ? area.titleEn : area.titleTr}
                  </h3>
                </div>
              ))}
            </div>
          </div>

          {/* Social Responsibility */}
          <div className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-8 text-center">
              {t('purpose.socialTitle')}
            </h2>
            <p className="text-gray-400 text-center mb-8 max-w-3xl mx-auto">
              {t('purpose.socialSubtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {socialKeys.map((key, index) => (
                <div
                  key={index}
                  className="group p-6 rounded-xl border border-white/10 bg-white/5 hover:border-[#4DFF88]/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#4DFF88]/10 flex items-center justify-center mb-4">
                    {key === 'environment' && <Leaf className="w-6 h-6 text-[#4DFF88]" />}
                    {key === 'intercultural' && <Globe className="w-6 h-6 text-[#4DFF88]" />}
                    {key === 'youth' && <Users className="w-6 h-6 text-[#4DFF88]" />}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {t(`purpose.social.${key}.title`)}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t(`purpose.social.${key}.desc`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PurposePage;
